/**
 * @fileoverview linkify — turns plain text into a list of React nodes
 * with clickable URLs, emails, and Brazilian-style phone numbers.
 *
 * Whitespace is preserved by the caller (use `whitespace-pre-wrap` on
 * the wrapping element). Returns an array of strings + anchor nodes
 * intermixed so the caller can render them inside any text container.
 */
import React from 'react';

const LINKIFY_RE = /(https?:\/\/[^\s)]+)|([\w.-]+@[\w-]+\.[\w.-]+)|(\(?\d{2}\)?\s?9?\d{4}-?\d{4})/g;

export function linkifyText(text: string): React.ReactNode[] {
  if (!text) return [];
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  // We must reset lastIndex on the regex since /g is stateful per-instance.
  // The literal above is fresh each call (function scope), so we're fine.
  while ((match = LINKIFY_RE.exec(text)) !== null) {
    if (match.index > lastIndex) out.push(text.slice(lastIndex, match.index));
    const [full, urlM, emailM, phoneM] = match;
    if (urlM) {
      out.push(
        <a
          key={key++}
          href={urlM}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-primary underline-offset-2 hover:underline break-all"
        >
          {urlM}
        </a>,
      );
    } else if (emailM) {
      out.push(
        <a
          key={key++}
          href={`mailto:${emailM}`}
          onClick={(e) => e.stopPropagation()}
          className="text-primary underline-offset-2 hover:underline"
        >
          {emailM}
        </a>,
      );
    } else if (phoneM) {
      const digits = phoneM.replace(/\D/g, '');
      out.push(
        <a
          key={key++}
          href={`tel:${digits}`}
          onClick={(e) => e.stopPropagation()}
          className="text-primary underline-offset-2 hover:underline"
        >
          {phoneM}
        </a>,
      );
    } else {
      out.push(full);
    }
    lastIndex = match.index + full.length;
  }
  // Reset for next call (safety — the regex is module-scope).
  LINKIFY_RE.lastIndex = 0;
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}
